import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
  InputAdornment
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { LOCALE_MAP } from 'utils/locale-formatting';
import { getVendors, deleteVendor } from 'services/vendor-service';
import { formatDateTimeToDateTimeBR, formatDateToDateBR } from 'utils/date_utils';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { useQueryClient } from 'react-query';

/**
 * VendorList Component
 * Displays vendors in a sortable, filterable table
 */
export const VendorList = ({ onEdit, tenant }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const SwalReact = withReactContent(Swal);
  const { t, i18n } = useTranslation(['vendor', 'common']);

  // Table state
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');
  const [tierFilter, setTierFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch vendors with filters (no pagination — load all for correct locale-aware sort)
  const { data, isLoading, error } = useQuery(
    ['vendors', tenant, tierFilter, searchTerm],
    () => getVendors(tenant, undefined, undefined, {
      tier: tierFilter || undefined,
      search: searchTerm || undefined,
      active: true
    }),
    {
      enabled: !!tenant,
      keepPreviousData: true
    }
  );

  const vendors = data?.data?.data || [];
  const total = data?.data?.pagination?.total || 0;

  // Sorting handler
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Delete handler
  const handleDelete = async (vendorId, vendorName) => {
    const result = await SwalReact.fire({
      title: t('vendor:notifications.deleteTitle'),
      text: t('vendor:notifications.deleteText', { name: vendorName }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d32f2f',
      cancelButtonColor: '#gray',
      confirmButtonText: t('vendor:notifications.deleteConfirm'),
      cancelButtonText: t('common:buttons.cancel')
    });

    if (result.isConfirmed) {
      try {
        await deleteVendor(vendorId);
        queryClient.invalidateQueries('vendors');
        SwalReact.fire({
          icon: 'success',
          title: t('vendor:notifications.deleteSuccess'),
          text: t('vendor:notifications.deleteSuccessText'),
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        SwalReact.fire({
          icon: 'error',
          title: t('vendor:notifications.errorTitle'),
          text: error.response?.data?.message || t('vendor:notifications.deleteError')
        });
      }
    }
  };

  // Get tier badge properties
  const getTierBadge = (tier) => {
    const badges = {
      1: { label: t('vendor:tier.1.label'), color: 'error' },
      2: { label: t('vendor:tier.2.label'), color: 'warning' },
      3: { label: t('vendor:tier.3.label'), color: 'success' }
    };
    return badges[tier] || { label: t('vendor:tier.unknown.label'), color: 'default' };
  };

  // Get trust score color
  const getTrustScoreColor = (score) => {
    if (!score) return 'default';
    if (score >= 90) return 'success';
    if (score >= 70) return 'warning';
    return 'error';
  };

  // Sort vendors locally (already filtered by backend)
  const sortedVendors = React.useMemo(() => {
    const locale = LOCALE_MAP[i18n.language] || 'pt-BR';
    const collator = new Intl.Collator(locale, { sensitivity: 'base' });
    return [...vendors].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Handle nested properties
      if (orderBy === 'lastAssessmentDate') {
        aValue = a.lastAssessment?.date || 0;
        bValue = b.lastAssessment?.date || 0;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return order === 'asc' ? collator.compare(aValue, bValue) : collator.compare(bValue, aValue);
      }

      if (aValue === bValue) return 0;
      if (order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [vendors, orderBy, order, i18n.language]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Typography color="error">{t('common:errors.generic')}: {error.message}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Filters */}
      <Box display="flex" gap={2} mb={3}>
        <TextField
          placeholder={t('vendor:table.filters.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>{t('vendor:table.filters.tierLabel')}</InputLabel>
          <Select
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
            label={t('vendor:table.filters.tierLabel')}
          >
            <MenuItem value="">{t('vendor:table.filters.allTiers')}</MenuItem>
            <MenuItem value="1">{t('vendor:table.filters.tier1')}</MenuItem>
            <MenuItem value="2">{t('vendor:table.filters.tier2')}</MenuItem>
            <MenuItem value="3">{t('vendor:table.filters.tier3')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Vendors Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  {t('vendor:table.columns.name')}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'tier'}
                  direction={orderBy === 'tier' ? order : 'asc'}
                  onClick={() => handleSort('tier')}
                >
                  {t('vendor:table.columns.tier')}
                </TableSortLabel>
              </TableCell>
              <TableCell>{t('vendor:table.columns.trustScore')}</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'lastAssessmentDate'}
                  direction={orderBy === 'lastAssessmentDate' ? order : 'asc'}
                  onClick={() => handleSort('lastAssessmentDate')}
                >
                  {t('vendor:table.columns.lastAssessment')}
                </TableSortLabel>
              </TableCell>
              <TableCell>{t('vendor:table.columns.dpa')}</TableCell>
              <TableCell>{t('vendor:table.columns.recertification')}</TableCell>
              <TableCell align="right">{t('common:table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedVendors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary">
                    {t('vendor:list.emptyState')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedVendors.map((vendor) => {
                const tierBadge = getTierBadge(vendor.tier);
                const trustScore = vendor.lastAssessment?.trustScore;

                return (
                  <TableRow key={vendor._id} hover>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {vendor.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {vendor.contactEmail}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tierBadge.label}
                        color={tierBadge.color}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {trustScore ? (
                        <Chip
                          label={`${trustScore}%`}
                          color={getTrustScoreColor(trustScore)}
                          size="small"
                        />
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          {t('vendor:table.noAssessment')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {vendor.lastAssessment?.date ? (
                        formatDateTimeToDateTimeBR(vendor.lastAssessment.date)
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          {t('vendor:table.never')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={vendor.dpaEstablished ? t('common:boolean.yes') : t('common:boolean.no')}
                        color={vendor.dpaEstablished ? 'success' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {vendor.recertificationDue ? (
                        <Chip label={t('vendor:detail.recertification.due', { date: '' }).replace(' — ', '')} color="error" size="small" variant="outlined" />
                      ) : vendor.recertificationDueDate ? (
                        <Chip
                          label={formatDateToDateBR(vendor.recertificationDueDate)}
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          {t('vendor:table.na')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('common:tooltips.view')}>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/vendors/${vendor._id}`)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common:tooltips.edit')}>
                        <IconButton
                          size="small"
                          onClick={() => onEdit && onEdit(vendor)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common:tooltips.delete')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(vendor._id, vendor.name)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Info */}
      <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="textSecondary">
          {t('vendor:list.count', { count: vendors.length, total })}
        </Typography>
      </Box>
    </Box>
  );
};
